import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TablePagination,
    TextField,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Typography,
    InputAdornment,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    Box
} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../../Services/ServiceLocator";
import { v4 as uuid } from 'uuid';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPencilAlt, faChevronUp, faChevronDown, faTrash, faPlus, faSave, faTimes, faSearch, faStore, faCreditCard, faUsers, faCog, faEdit} from "@fortawesome/free-solid-svg-icons";
import { AuthenticationService } from "../../../Services/AuthenticationService";
import { filterPersistenceService } from "../../../Services/FilterPersistenceService";
import { getNumberInputDisplayValue, parseNumberInputValue } from '../../../utils/numberInputUtils';
import DrawerMenuRight from "../../Common/DrawerMenuRight";
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    filterToggle: {
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '2px 10px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        minWidth: '150px',
        '& .MuiCheckbox-root': {
            padding: '0px',
            color: '#35d2d9',
            '&.Mui-checked': {
                color: '#35d2d9',
            },
        }
    },
    filterLabel: {
        marginLeft: '8px',
        fontSize: '14px',
        color: 'rgba(0, 0, 0, 0.87)',
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
    },
    toolbarRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    sortableHeader: {
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background-color 0.2s ease',
    },
    editableCell: {
        padding: '8px',
        minWidth: '150px'
    },
    editableInput: {
        width: '100%'
    },
    actionButtons: {
        display: 'flex',
        gap: '8px'
    },
    tabContent: {
        marginTop: '16px'
    }
});

class Lists extends React.Component {
    state = {
        loading: true,
        user: {},
        vendors: [],
        dealers: [],
        originalDealers: [],
        paymentTerms: [],
        page: 0,
        rowsPerPage: 100,
        searchTerm: '',
        sortBy: 'name',
        sortDirection: 'asc',
        activeTab: 0,
        
        // Bulk Edit States
        showVendorBulkEdit: false,
        pendingVendors: [],
        showDealerBulkEdit: false,
        pendingDealers: [],
        showPaymentTermsBulkEdit: false,
        pendingPaymentTerms: [],
        savingNewValue: false,
        
        // Brand Management States
        brands: [],
        editingBrand: null,
        newBrandValue: '',
        oldBrandValue: '',
        editBrandOpen: false,
        savingBrand: false,

        partsExpanded: [],
        bulkLeadTimeDrawerOpen: false,
        bulkLeadTimeVendorId: '',
        bulkLeadTimeWeeks: '',
        bulkLeadTimeTargetParts: [],
        bulkLeadTimeConfirmText: '',
        savingBulkLeadTime: false
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    componentDidMount() {
        this.loadSavedFilterSettings();
        this.loadData();
    }

    loadSavedFilterSettings = () => {
        const savedSettings = filterPersistenceService.loadFilterSettings('lists');
        if (savedSettings) {
            this.setState({
                page: savedSettings.page || 0,
                rowsPerPage: savedSettings.rowsPerPage || 100,
                searchTerm: savedSettings.searchTerm || '',
                sortBy: savedSettings.sortBy || 'name',
                sortDirection: savedSettings.sortDirection || 'asc'
            });
        }
    }

    saveFilterSettings = () => {
        const settings = {
            page: this.state.page,
            rowsPerPage: this.state.rowsPerPage,
            searchTerm: this.state.searchTerm,
            sortBy: this.state.sortBy,
            sortDirection: this.state.sortDirection
        };
        filterPersistenceService.saveFilterSettings('lists', settings);
    }

    handleChangePage = (event, newPage) => {
        this.setState({page: newPage}, () => {
            this.saveFilterSettings();
        });
    };

    handleChangeRowsPerPage = (event) => {
        this.setState({
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        }, () => {
            this.saveFilterSettings();
        });
    };

    loadData = async () => {
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('EditAssembliesAndParts')) {
            globalProps.history.push('/admin/parts');
            return;
        }

        try {
            const vendorService = ServiceLocator.Instance.getVendorService();
            const partService = ServiceLocator.Instance.getPartService();
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            
            const [vendors, dealers, paymentTerms, partsResult, assembliesResult] = await Promise.all([
                vendorService.getAllVendors(),
                vendorService.getSalesLogSources(),
                vendorService.getAllPaymentTerms(),
                partService.getAllPartsExpanded(),
                assemblyService.getAllAssembliesExpanded()
            ]);

            // Collect unique brands from parts and assemblies
            const brandSet = new Set();
            partsResult.forEach(p => {
                if (p.part && p.part.brand && p.part.brand.trim() !== '') {
                    brandSet.add(p.part.brand);
                }
            });
            assembliesResult.forEach(a => {
                // Handle both assembly and subassembly structures
                if (a.assembly && a.assembly.brand && a.assembly.brand.trim() !== '') {
                    brandSet.add(a.assembly.brand);
                }
                if (a.subAssembly && a.subAssembly.brand && a.subAssembly.brand.trim() !== '') {
                    brandSet.add(a.subAssembly.brand);
                }
            });
            const brands = Array.from(brandSet).sort();

            this.setState({
                loading: false,
                user,
                vendors,
                dealers,
                originalDealers: [...dealers], // Store original data for change detection
                paymentTerms: paymentTerms.filter(pt => pt.isActive),
                brands,
                partsExpanded: partsResult || []
            });
        } catch (error) {
            console.error('Error loading data:', error);
            this.setState({ loading: false });
        }
    };

    searchVendor = (event) => {
        this.setState({ searchTerm: event.target.value, page: 0 }, () => {
            this.saveFilterSettings();
        });
    };

    handleSort = (field) => {
        const { sortBy, sortDirection } = this.state;
        const newDirection = sortBy === field && sortDirection === 'asc' ? 'desc' : 'asc';
        this.setState({ sortBy: field, sortDirection: newDirection }, () => {
            this.saveFilterSettings();
        });
    };


    handleDeleteVendor = async (vendor) => {
        if (!window.confirm(`Are you sure you want to delete vendor "${vendor.name}"?`)) {
            return;
        }

        try {
            const vendorService = ServiceLocator.Instance.getVendorService();
            await vendorService.deleteVendor(vendor.id);
            
            this.setState(prevState => ({
                vendors: prevState.vendors.filter(v => v.id !== vendor.id)
            }));
        } catch (error) {
            console.error('Error deleting vendor:', error);
            window.$alertError('Error deleting vendor: ' + error.message);
        }
    };

    handleDeleteDealer = async (dealer) => {
        if (!window.confirm(`Are you sure you want to delete dealer "${dealer.source}"?`)) {
            return;
        }

        try {
            const vendorService = ServiceLocator.Instance.getVendorService();
            await vendorService.deleteSalesLogSource(dealer.id);
            
            this.setState(prevState => ({
                dealers: prevState.dealers.filter(d => d.id !== dealer.id)
            }));
        } catch (error) {
            console.error('Error deleting dealer:', error);
            window.$alertError('Error deleting dealer: ' + error.message);
        }
    };



    handleVendorBulkFieldChange = (vendorId, field, value) => {
        this.setState(prevState => ({
            vendors: prevState.vendors.map(vendor => 
                vendor.id === vendorId 
                    ? { ...vendor, [field]: value }
                    : vendor
            )
        }));
    };

    handleDealerBulkFieldChange = (dealerId, field, value) => {
        this.setState(prevState => ({
            dealers: prevState.dealers.map(dealer => 
                dealer.id === dealerId 
                    ? { ...dealer, [field]: value }
                    : dealer
            )
        }));
    };


    handleDeletePaymentTerms = async (paymentTerms) => {
        if (!window.confirm(`Are you sure you want to deactivate payment terms "${paymentTerms.name}"?`)) {
            return;
        }

        try {
            const vendorService = ServiceLocator.Instance.getVendorService();
            const updatedPaymentTerms = {
                ...paymentTerms,
                isActive: false
            };
            const savedPaymentTerms = await vendorService.updatePaymentTerms(updatedPaymentTerms);
            
            this.setState(prevState => ({
                paymentTerms: prevState.paymentTerms.filter(pt => pt.id !== paymentTerms.id)
            }));
        } catch (error) {
            console.error('Error deactivating payment terms:', error);
            window.$alertError('Error deactivating payment terms: ' + error.message);
        }
    };

    handlePaymentTermsBulkFieldChange = (paymentTermsId, field, value) => {
        this.setState(prevState => ({
            paymentTerms: prevState.paymentTerms.map(pt => 
                pt.id === paymentTermsId 
                    ? { ...pt, [field]: value }
                    : pt
            )
        }));
    };

    handleTabChange = (event, newValue) => {
        this.setState({ activeTab: newValue });
    };

    // Vendor Bulk Edit Methods
    addVendorBulkLine = () => {
        const newLine = {
            id: uuid(),
            name: '',
            website: '',
            phoneNumber: '',
            address: '',
            primaryContact: '',
            primaryEmail: '',
            secondaryContact: '',
            secondaryEmail: '',
            paymentTermsId: null,
            isComplete: false
        };
        
        this.setState(prevState => ({
            pendingVendors: [...prevState.pendingVendors, newLine]
        }));
    };

    handleVendorBulkChange = (lineId, field, value) => {
        this.setState(prevState => ({
            pendingVendors: prevState.pendingVendors.map(line => 
                line.id === lineId 
                    ? { ...line, [field]: value, isComplete: !!(line.name && line.name.trim()) }
                    : line
            )
        }));
    };

    removeVendorBulkLine = (lineId) => {
        this.setState(prevState => ({
            pendingVendors: prevState.pendingVendors.filter(line => line.id !== lineId)
        }));
    };

    handleVendorBulkKeyDown = (lineId, event) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            // Get the current field name from the input element
            const currentField = event.target.name;
            
            // Define the field order for tabbing
            const fieldOrder = ['name', 'website', 'paymentTermsId', 'phoneNumber', 'address', 'primaryContact', 'primaryEmail', 'secondaryContact', 'secondaryEmail'];
            const currentFieldIndex = fieldOrder.indexOf(currentField);
            
            // If we're on the last field, go to the name field of the next line (or create one)
            if (currentFieldIndex === fieldOrder.length - 1) {
                event.preventDefault();
                
                // Find current line index
                const currentLineIndex = this.state.pendingVendors.findIndex(line => line.id === lineId);
                
                // If there's a next line, focus its name field
                if (currentLineIndex < this.state.pendingVendors.length - 1) {
                    const nextLineId = this.state.pendingVendors[currentLineIndex + 1].id;
                    
                    setTimeout(() => {
                        const nameField = document.querySelector(`[data-vendor-line-id="${nextLineId}"] input[name="name"]`);
                        if (nameField) {
                            nameField.focus();
                            nameField.select();
                        }
                    }, 100);
                } else {
                    // No next line exists, create one and focus its name field
                    this.addVendorBulkLine();
                    
                    setTimeout(() => {
                        const newLineId = this.state.pendingVendors[this.state.pendingVendors.length - 1]?.id;
                        if (newLineId) {
                            const nameField = document.querySelector(`[data-vendor-line-id="${newLineId}"] input[name="name"]`);
                            if (nameField) {
                                nameField.focus();
                                nameField.select();
                            }
                        }
                    }, 100);
                }
            }
            // Otherwise, let the default tab behavior handle moving to the next field
        }
    };

    saveAllVendorBulkChanges = async () => {
        const self = this;
        const vendorService = ServiceLocator.Instance.getVendorService();
        
        self.setState({savingNewValue: true});
        
        try {
            // Save all existing vendors that have been modified
            for (const vendor of this.state.vendors) {
                await vendorService.updateVendor(vendor);
            }
            
            // Get all new vendors that have been filled out
            const vendorsToAdd = this.state.pendingVendors
                .filter(line => line.name && line.name.trim())
                .map(line => ({
                    name: line.name.trim(),
                    website: line.website || '',
                    phoneNumber: line.phoneNumber || '',
                    address: line.address || '',
                    primaryContact: line.primaryContact || '',
                    primaryEmail: line.primaryEmail || '',
                    secondaryContact: line.secondaryContact || '',
                    secondaryEmail: line.secondaryEmail || '',
                    paymentTermsId: line.paymentTermsId || null
                }));
            
            // Add all new vendors
            const newVendors = await Promise.all(
                vendorsToAdd.map(vendorData => vendorService.createVendor(vendorData))
            );
            
            // Keep the current vendors state (already updated) and add the new ones
            self.setState(prevState => ({
                vendors: [...prevState.vendors, ...newVendors],
                savingNewValue: false,
                showVendorBulkEdit: false,
                pendingVendors: []
            }));
            
            const totalChanges = this.state.vendors.length + vendorsToAdd.length;
            window.$alertSuccess(`${totalChanges} vendor${totalChanges > 1 ? 's' : ''} saved successfully`);
        } catch (error) {
            console.error('Error saving vendors:', error);
            self.setState({savingNewValue: false});
            window.$alertError('Error saving vendors');
        }
    };

    cancelVendorBulkEdit = () => {
        this.setState({
            showVendorBulkEdit: false,
            pendingVendors: []
        });
    };

    // Dealer Bulk Edit Methods
    addDealerBulkLine = () => {
        const newLine = {
            id: uuid(),
            source: '',
            website: '',
            phoneNumber: '',
            address: '',
            primaryContact: '',
            primaryEmail: '',
            secondaryContact: '',
            secondaryEmail: '',
            isComplete: false
        };
        
        this.setState(prevState => ({
            pendingDealers: [...prevState.pendingDealers, newLine]
        }));
    };

    handleDealerBulkChange = (lineId, field, value) => {
        this.setState(prevState => ({
            pendingDealers: prevState.pendingDealers.map(line => 
                line.id === lineId 
                    ? { ...line, [field]: value, isComplete: !!(line.source && line.source.trim()) }
                    : line
            )
        }));
    };

    removeDealerBulkLine = (lineId) => {
        this.setState(prevState => ({
            pendingDealers: prevState.pendingDealers.filter(line => line.id !== lineId)
        }));
    };

    handleDealerBulkKeyDown = (lineId, event) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            const currentField = event.target.name;
            const fieldOrder = ['source', 'website', 'phoneNumber', 'address', 'primaryContact', 'primaryEmail', 'secondaryContact', 'secondaryEmail'];
            const currentFieldIndex = fieldOrder.indexOf(currentField);
            
            if (currentFieldIndex === fieldOrder.length - 1) {
                event.preventDefault();
                
                const currentLineIndex = this.state.pendingDealers.findIndex(line => line.id === lineId);
                
                if (currentLineIndex < this.state.pendingDealers.length - 1) {
                    const nextLineId = this.state.pendingDealers[currentLineIndex + 1].id;
                    
                    setTimeout(() => {
                        const sourceField = document.querySelector(`[data-dealer-line-id="${nextLineId}"] input[name="source"]`);
                        if (sourceField) {
                            sourceField.focus();
                            sourceField.select();
                        }
                    }, 100);
                } else {
                    this.addDealerBulkLine();
                    
                    setTimeout(() => {
                        const newLineId = this.state.pendingDealers[this.state.pendingDealers.length - 1]?.id;
                        if (newLineId) {
                            const sourceField = document.querySelector(`[data-dealer-line-id="${newLineId}"] input[name="source"]`);
                            if (sourceField) {
                                sourceField.focus();
                                sourceField.select();
                            }
                        }
                    }, 100);
                }
            }
        }
    };

    saveAllDealerBulkChanges = async () => {
        const self = this;
        const vendorService = ServiceLocator.Instance.getVendorService();
        
        self.setState({savingNewValue: true});
        
        try {
            // Save all existing dealers that have been modified
            // We need to compare with the original data to see what changed
            const originalDealers = this.state.originalDealers || [];
            const modifiedDealers = this.state.dealers.filter(dealer => {
                const original = originalDealers.find(orig => orig.id === dealer.id);
                if (!original) return true; // New dealer
                
                // Check if any field has changed
                return dealer.source !== original.source ||
                       dealer.website !== original.website ||
                       dealer.phoneNumber !== original.phoneNumber ||
                       dealer.address !== original.address ||
                       dealer.primaryContact !== original.primaryContact ||
                       dealer.primaryEmail !== original.primaryEmail ||
                       dealer.secondaryContact !== original.secondaryContact ||
                       dealer.secondaryEmail !== original.secondaryEmail;
            });
            
            // Update only the modified dealers
            for (const dealer of modifiedDealers) {
                await vendorService.updateSalesLogSource(dealer);
            }
            
            // Get all new dealers that have been filled out
            const dealersToAdd = this.state.pendingDealers
                .filter(line => line.source && line.source.trim())
                .map(line => ({
                    source: line.source.trim(),
                    website: line.website || '',
                    phoneNumber: line.phoneNumber || '',
                    address: line.address || '',
                    primaryContact: line.primaryContact || '',
                    primaryEmail: line.primaryEmail || '',
                    secondaryContact: line.secondaryContact || '',
                    secondaryEmail: line.secondaryEmail || ''
                }));
            
            // Add all new dealers
            const newDealers = await Promise.all(
                dealersToAdd.map(dealerData => vendorService.createSalesLogSource(dealerData))
            );
            
            // Keep the current dealers state (already updated) and add the new ones
            self.setState(prevState => ({
                dealers: [...prevState.dealers, ...newDealers],
                savingNewValue: false,
                showDealerBulkEdit: false,
                pendingDealers: []
            }));
            
            const totalChanges = this.state.dealers.length + dealersToAdd.length;
            window.$alertSuccess(`${totalChanges} dealer${totalChanges > 1 ? 's' : ''} saved successfully`);
        } catch (error) {
            console.error('Error saving dealers:', error);
            self.setState({savingNewValue: false});
            window.$alertError('Error saving dealers');
        }
    };

    cancelDealerBulkEdit = () => {
        this.setState({
            showDealerBulkEdit: false,
            pendingDealers: []
        });
    };

    // Payment Terms Bulk Edit Methods
    addPaymentTermsBulkLine = () => {
        const newLine = {
            id: uuid(),
            name: '',
            description: '',
            days: '',
            isComplete: false
        };
        
        this.setState(prevState => ({
            pendingPaymentTerms: [...prevState.pendingPaymentTerms, newLine]
        }));
    };

    handlePaymentTermsBulkChange = (lineId, field, value) => {
        this.setState(prevState => ({
            pendingPaymentTerms: prevState.pendingPaymentTerms.map(line => 
                line.id === lineId 
                    ? { ...line, [field]: value, isComplete: !!(line.name && line.name.trim()) }
                    : line
            )
        }));
    };

    removePaymentTermsBulkLine = (lineId) => {
        this.setState(prevState => ({
            pendingPaymentTerms: prevState.pendingPaymentTerms.filter(line => line.id !== lineId)
        }));
    };

    handlePaymentTermsBulkKeyDown = (lineId, event) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            // Get the current field name from the input element
            const currentField = event.target.name;
            
            // Define the field order for tabbing
            const fieldOrder = ['name', 'description', 'days'];
            const currentFieldIndex = fieldOrder.indexOf(currentField);
            
            // If we're on the last field, go to the name field of the next line (or create one)
            if (currentFieldIndex === fieldOrder.length - 1) {
                event.preventDefault();
                
                // Find current line index
                const currentLineIndex = this.state.pendingPaymentTerms.findIndex(line => line.id === lineId);
                
                // If there's a next line, focus its name field
                if (currentLineIndex < this.state.pendingPaymentTerms.length - 1) {
                    const nextLineId = this.state.pendingPaymentTerms[currentLineIndex + 1].id;
                    
                    setTimeout(() => {
                        const nameField = document.querySelector(`[data-payment-terms-line-id="${nextLineId}"] input[name="name"]`);
                        if (nameField) {
                            nameField.focus();
                            nameField.select();
                        }
                    }, 100);
                } else {
                    // No next line exists, create one and focus its name field
                    this.addPaymentTermsBulkLine();
                    
                    setTimeout(() => {
                        const newLineId = this.state.pendingPaymentTerms[this.state.pendingPaymentTerms.length - 1]?.id;
                        if (newLineId) {
                            const nameField = document.querySelector(`[data-payment-terms-line-id="${newLineId}"] input[name="name"]`);
                            if (nameField) {
                                nameField.focus();
                                nameField.select();
                            }
                        }
                    }, 100);
                }
            }
            // Otherwise, let the default tab behavior handle moving to the next field
        }
    };

    saveAllPaymentTermsBulkChanges = async () => {
        const self = this;
        const vendorService = ServiceLocator.Instance.getVendorService();
        
        self.setState({savingNewValue: true});
        
        try {
            // Save all existing payment terms that have been modified
            for (const paymentTerms of this.state.paymentTerms) {
                const paymentTermsToSave = {
                    ...paymentTerms,
                    days: parseNumberInputValue(paymentTerms.days)
                };
                await vendorService.updatePaymentTerms(paymentTermsToSave);
            }
            
            // Get all new payment terms that have been filled out
            const paymentTermsToAdd = this.state.pendingPaymentTerms
                .filter(line => line.name && line.name.trim())
                .map(line => ({
                    name: line.name.trim(),
                    description: line.description || '',
                    days: parseNumberInputValue(line.days) || 0,
                    isActive: true
                }));
            
            // Add all new payment terms
            const newPaymentTerms = await Promise.all(
                paymentTermsToAdd.map(paymentTermsData => vendorService.createPaymentTerms(paymentTermsData))
            );
            
            // Keep the current payment terms state (already updated) and add the new ones
            self.setState(prevState => ({
                paymentTerms: [...prevState.paymentTerms, ...newPaymentTerms],
                savingNewValue: false,
                showPaymentTermsBulkEdit: false,
                pendingPaymentTerms: []
            }));
            
            const totalChanges = this.state.paymentTerms.length + paymentTermsToAdd.length;
            window.$alertSuccess(`${totalChanges} payment term${totalChanges > 1 ? 's' : ''} saved successfully`);
        } catch (error) {
            console.error('Error saving payment terms:', error);
            self.setState({savingNewValue: false});
            window.$alertError('Error saving payment terms');
        }
    };

    cancelPaymentTermsBulkEdit = () => {
        this.setState({
            showPaymentTermsBulkEdit: false,
            pendingPaymentTerms: []
        });
    };

    getSortedAndFilteredVendors = () => {
        const { vendors, searchTerm, sortBy, sortDirection } = this.state;
        
        let filtered = vendors;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = vendors.filter(vendor => 
                vendor.name.toLowerCase().includes(term) ||
                (vendor.primaryContact && vendor.primaryContact.toLowerCase().includes(term)) ||
                (vendor.primaryEmail && vendor.primaryEmail.toLowerCase().includes(term)) ||
                (vendor.secondaryContact && vendor.secondaryContact.toLowerCase().includes(term)) ||
                (vendor.secondaryEmail && vendor.secondaryEmail.toLowerCase().includes(term))
            );
        }

        return filtered.sort((a, b) => {
            let aValue = a[sortBy] || '';
            let bValue = b[sortBy] || '';
            
            if (sortBy === 'paymentTerms') {
                aValue = a.paymentTerms?.name || '';
                bValue = b.paymentTerms?.name || '';
            }
            
            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();
            
            if (sortDirection === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });
    };

    getSortedAndFilteredDealers = () => {
        const { dealers, searchTerm, sortBy, sortDirection } = this.state;
        
        let filtered = dealers;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = dealers.filter(dealer => 
                dealer.source.toLowerCase().includes(term) ||
                (dealer.primaryContact && dealer.primaryContact.toLowerCase().includes(term)) ||
                (dealer.primaryEmail && dealer.primaryEmail.toLowerCase().includes(term)) ||
                (dealer.secondaryContact && dealer.secondaryContact.toLowerCase().includes(term)) ||
                (dealer.secondaryEmail && dealer.secondaryEmail.toLowerCase().includes(term))
            );
        }

        return filtered.sort((a, b) => {
            let aValue = a[sortBy] || '';
            let bValue = b[sortBy] || '';
            
            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();
            
            if (sortDirection === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });
    };

    // Brand Management Methods
    handleBrandClick = (brand) => {
        this.setState({
            editBrandOpen: true,
            newBrandValue: brand,
            oldBrandValue: brand
        });
    };

    saveBrand = async () => {
        const self = this;
        const { newBrandValue, oldBrandValue } = this.state;
        
        if (!newBrandValue || newBrandValue.trim() === '') {
            window.$alertError('Brand name cannot be empty');
            return;
        }
        
        self.setState({ savingBrand: true });
        
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            
            // Update brands in both parts and assemblies
            await Promise.all([
                partService.updateBrand(newBrandValue, oldBrandValue),
                assemblyService.updateBrand(newBrandValue, oldBrandValue)
            ]);
            
            // Reload data
            await self.loadData();
            
            self.setState({
                editBrandOpen: false,
                newBrandValue: '',
                oldBrandValue: '',
                savingBrand: false
            });
            
            window.$alertSuccess('Brand updated successfully');
        } catch (error) {
            console.error('Error saving brand:', error);
            self.setState({ savingBrand: false });
            window.$alertError('Error saving brand');
        }
    };

    getNormalizedFileUrls = (fileURLs) => {
        if (!fileURLs) return '';
        if (typeof fileURLs === 'string') return fileURLs;
        if (Array.isArray(fileURLs)) return fileURLs.filter(x => x && x.trim() !== '').join('~|~');
        return '';
    };

    getLatestRevisionOnly = (parts) => {
        const latestRevisionMap = new Map();
        parts.forEach((partExpanded) => {
            const pn = partExpanded.part.partId;
            const existing = latestRevisionMap.get(pn);
            if (!existing || partExpanded.part.revision > existing.part.revision) {
                latestRevisionMap.set(pn, partExpanded);
            }
        });
        return Array.from(latestRevisionMap.values());
    };

    getVendorNameById = (vendorId) => {
        if (!vendorId) return '';
        const vendor = (this.state.vendors || []).find(v => String(v.id) === String(vendorId));
        return vendor?.name || '';
    };

    getVendorTargetParts = (vendorId) => {
        if (!vendorId) return [];
        let matching = (this.state.partsExpanded || []).filter((p) => {
            if (!p?.part || !p.part.isActive) return false;
            const selectedVendorId = p.part.selectedVendorId;
            const hasVendor = (p.part.vendorParts || []).some(vp => String(vp.vendorId) === String(vendorId));
            const selectedVendorMatches = selectedVendorId != null && String(selectedVendorId) === String(vendorId);
            const noSelectedVendorButHasMatch = selectedVendorId == null && hasVendor;
            return selectedVendorMatches || noSelectedVendorButHasMatch;
        });

        matching = this.getLatestRevisionOnly(matching);

        return matching.sort((a, b) => {
            if (a.part.partId < b.part.partId) return -1;
            if (a.part.partId > b.part.partId) return 1;
            if (a.part.revision > b.part.revision) return -1;
            if (a.part.revision < b.part.revision) return 1;
            return 0;
        });
    };

    openBulkLeadTimeForVendor = () => {
        const vendorId = this.state.bulkLeadTimeVendorId || '';
        const targetParts = this.getVendorTargetParts(vendorId);

        this.setState({
            bulkLeadTimeDrawerOpen: true,
            bulkLeadTimeVendorId: vendorId,
            bulkLeadTimeWeeks: '',
            bulkLeadTimeConfirmText: '',
            bulkLeadTimeTargetParts: targetParts
        });
    };

    closeBulkLeadTimeDrawer = () => {
        this.setState({
            bulkLeadTimeDrawerOpen: false,
            bulkLeadTimeWeeks: '',
            bulkLeadTimeConfirmText: '',
            bulkLeadTimeTargetParts: [],
            savingBulkLeadTime: false
        });
    };

    handleBulkLeadTimeVendorChange = (event) => {
        const vendorId = event.target.value;
        this.setState({
            bulkLeadTimeVendorId: vendorId,
            bulkLeadTimeTargetParts: this.getVendorTargetParts(vendorId)
        });
    };

    applyBulkLeadTimeUpdate = async () => {
        const leadTimeWeeks = parseNumberInputValue(this.state.bulkLeadTimeWeeks);
        if (!Number.isFinite(leadTimeWeeks) || leadTimeWeeks <= 0) {
            window.$alertError('Enter a valid lead time in weeks.');
            return;
        }

        if (!this.state.bulkLeadTimeVendorId) {
            window.$alertError('Select a vendor first.');
            return;
        }

        const targetParts = this.state.bulkLeadTimeTargetParts || [];
        if (targetParts.length === 0) {
            window.$alertError('No parts match this update request.');
            return;
        }

        if ((this.state.bulkLeadTimeConfirmText || '').trim().toUpperCase() !== 'UPDATE') {
            window.$alertError('Type UPDATE to confirm this bulk lead time change.');
            return;
        }

        this.setState({ savingBulkLeadTime: true });

        try {
            const partService = ServiceLocator.Instance.getPartService();
            await Promise.all(targetParts.map((partExpanded) => {
                const updatedPart = {
                    ...partExpanded.part,
                    leadTimeInWeeks: leadTimeWeeks,
                    fileURLs: this.getNormalizedFileUrls(partExpanded.part.fileURLs)
                };
                return partService.updatePart(updatedPart);
            }));

            await this.loadData();
            this.setState({
                bulkLeadTimeDrawerOpen: false,
                bulkLeadTimeWeeks: '',
                bulkLeadTimeConfirmText: '',
                bulkLeadTimeTargetParts: [],
                savingBulkLeadTime: false
            });

            const vendorLabel = this.getVendorNameById(this.state.bulkLeadTimeVendorId);
            window.$alertSuccess(`Updated lead time to ${leadTimeWeeks} week(s) for ${targetParts.length} part(s) for vendor "${vendorLabel}".`);
        } catch (error) {
            console.error('Error updating lead times in bulk:', error);
            window.$alertError('Failed to update lead times.');
            this.setState({ savingBulkLeadTime: false });
        }
    };

    render() {
        const { classes } = this.props;
        const {
            loading,
            vendors,
            dealers,
            paymentTerms,
            page,
            rowsPerPage,
            searchTerm,
            sortBy,
            sortDirection,
            editingVendor,
            newVendor,
            showAddDialog,
            editingPaymentTerms,
            newPaymentTerms,
            showPaymentTermsDialog,
            activeTab,
            brands,
            editBrandOpen,
            newBrandValue,
            oldBrandValue,
            savingBrand
        } = this.state;

        if (loading) {
            return (
                <DashboardTemplate title="Lists" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <CircularProgress />
                    </div>
                </DashboardTemplate>
            );
        }

        const sortedVendors = this.getSortedAndFilteredVendors();
        const paginatedVendors = sortedVendors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        const vendorLeadTimeOptions = (vendors || [])
            .filter(v => v?.id && v?.name)
            .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        const bulkLeadTimeTargetParts = this.state.bulkLeadTimeTargetParts || [];
        
        const sortedDealers = this.getSortedAndFilteredDealers();
        const paginatedDealers = sortedDealers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        const SortButton = ({ field, children }) => (
            <th 
                className={classes.sortableHeader}
                onClick={() => this.handleSort(field)}
            >
                {children}
                {sortBy === field && (
                    <FontAwesomeIcon 
                        icon={sortDirection === 'asc' ? faChevronUp : faChevronDown} 
                        size="sm"
                        style={{ marginLeft: '8px' }}
                    />
                )}
            </th>
        );

        return (
            <DashboardTemplate title="Lists" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div className="container">
                        <Tabs value={activeTab} onChange={this.handleTabChange} style={{marginTop: '0'}}>
                            <Tab 
                                label={
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                        <FontAwesomeIcon icon={faStore} />
                                        Vendors
                                    </div>
                                } 
                            />
                            <Tab 
                                label={
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                        <FontAwesomeIcon icon={faCreditCard} />
                                        Payment Terms
                                    </div>
                                } 
                            />
                            <Tab 
                                label={
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                        <FontAwesomeIcon icon={faUsers} />
                                        Dealers
                                    </div>
                                } 
                            />
                            <Tab 
                                label={
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                        <FontAwesomeIcon icon={faCog} />
                                        Brands
                                    </div>
                                } 
                            />
                        </Tabs>
                        <Box className={classes.tabContent}>
                            {activeTab === 0 && (
                                <>
                                    <div className={`${classes.toolbar} toolbar-responsive`}>
                                        <div className={`${classes.toolbarLeft} toolbar-left`} style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, maxWidth: '40%' }}>
                                            <TextField
                                                value={searchTerm}
                                                onChange={this.searchVendor}
                                                label="Search Vendors..."
                                                variant="outlined"
                                                className="search-field"
                                                style={{minWidth: '300px'}}
                                            />
                                        </div>
                                        <div className={`${classes.toolbarRight} toolbar-right`} style={{ flex: 1, maxWidth: '40%', justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={this.openBulkLeadTimeForVendor}
                                                className="success-button-lg action-button"
                                                style={{ minWidth: 150 }}
                                            >
                                                Lead Time by Vendor
                                            </Button>
                                        </div>
                                    </div>
                                <center>
                                    <TablePagination
                                        rowsPerPageOptions={[50, 100, 200, 500]}
                                        colSpan={10}
                                        count={sortedVendors.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        slotProps={{
                                            select: {
                                                'aria-label': 'rows per page',
                                            },
                                            actions: {
                                                showFirstButton: true,
                                                showLastButton: true,
                                                slots: {
                                                    firstPageIcon: FirstPageIcon,
                                                    lastPageIcon: LastPageIcon,
                                                    nextPageIcon: ChevronRightIcon,
                                                    backPageIcon: ChevronLeftIcon,
                                                },
                                            },
                                        }}
                                        onChangePage={this.handleChangePage}
                                        onChangeRowsPerPage={this.handleChangeRowsPerPage}
                                    />
                                </center>
                                <div className="parts-table-container">
                                    <table className="parts-table">
                                        <thead>
                                            <tr>
                                                <SortButton field="name">Name</SortButton>
                                                <SortButton field="paymentTerms">Payment Terms</SortButton>
                                                <th>Website</th>
                                                <th>Phone</th>
                                                <th>Address</th>
                                                <th>Primary Contact</th>
                                                <th>Primary Email</th>
                                                <th>Secondary Contact</th>
                                                <th>Secondary Email</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!this.state.showVendorBulkEdit ? (
                                                // Normal view - show existing vendors
                                                paginatedVendors.map((vendor) => (
                                                    <tr key={vendor.id}>
                                                        <td>{vendor.name}</td>
                                                        <td>{vendor.paymentTerms?.name || ''}</td>
                                                        <td>
                                                            {vendor.website && vendor.website.length > 0 ? 
                                                                <a href={vendor.website} target="_blank" rel="noopener noreferrer">{vendor.website}</a> : 
                                                                ''
                                                            }
                                                        </td>
                                                        <td>{vendor.phoneNumber || ''}</td>
                                                        <td>{vendor.address || ''}</td>
                                                        <td>{vendor.primaryContact || ''}</td>
                                                        <td>{vendor.primaryEmail || ''}</td>
                                                        <td>{vendor.secondaryContact || ''}</td>
                                                        <td>{vendor.secondaryEmail || ''}</td>
                                                        <td>
                                                            <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                <Tooltip title="Delete">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="secondary"
                                                                        onClick={() => this.handleDeleteVendor(vendor)}
                                                                        tabIndex={-1}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                // Bulk edit view - show all vendors in edit mode plus new lines
                                                <>
                                                    {/* Existing vendors in edit mode */}
                                                    {paginatedVendors.map((vendor) => (
                                                        <tr key={vendor.id}>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.name}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'name', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <FormControl variant="outlined" size="small" className={classes.editableInput}>
                                                                    <Select
                                                                        value={vendor.paymentTermsId || ''}
                                                                        onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'paymentTermsId', e.target.value)}
                                                                    >
                                                                        <MenuItem value="">
                                                                            <em>None</em>
                                                                        </MenuItem>
                                                                        {paymentTerms.map((pt) => (
                                                                            <MenuItem key={pt.id} value={pt.id}>
                                                                                {pt.name}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.website || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'website', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.phoneNumber || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'phoneNumber', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.address || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'address', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.primaryContact || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'primaryContact', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.primaryEmail || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'primaryEmail', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.secondaryContact || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'secondaryContact', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={vendor.secondaryEmail || ''}
                                                                    onChange={(e) => this.handleVendorBulkFieldChange(vendor.id, 'secondaryEmail', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                    <Tooltip title="Delete">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="secondary"
                                                                            onClick={() => this.handleDeleteVendor(vendor)}
                                                                            tabIndex={-1}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    
                                                    {/* New vendor lines */}
                                                    {this.state.pendingVendors.map((line, index) => (
                                                        <tr key={line.id} data-vendor-line-id={line.id} style={{backgroundColor: '#f5f5f5'}}>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="name"
                                                                    value={line.name}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'name', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    autoFocus={index === this.state.pendingVendors.length - 1}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <FormControl variant="outlined" size="small" className={classes.editableInput}>
                                                                    <Select
                                                                        name="paymentTermsId"
                                                                        value={line.paymentTermsId || ''}
                                                                        onChange={(e) => this.handleVendorBulkChange(line.id, 'paymentTermsId', e.target.value)}
                                                                        onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    >
                                                                        <MenuItem value="">
                                                                            <em>None</em>
                                                                        </MenuItem>
                                                                        {paymentTerms.map((pt) => (
                                                                            <MenuItem key={pt.id} value={pt.id}>
                                                                                {pt.name}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="website"
                                                                    value={line.website}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'website', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="phoneNumber"
                                                                    value={line.phoneNumber}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'phoneNumber', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="address"
                                                                    value={line.address}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'address', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="primaryContact"
                                                                    value={line.primaryContact}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'primaryContact', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="primaryEmail"
                                                                    value={line.primaryEmail}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'primaryEmail', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="secondaryContact"
                                                                    value={line.secondaryContact}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'secondaryContact', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="secondaryEmail"
                                                                    value={line.secondaryEmail}
                                                                    onChange={(e) => this.handleVendorBulkChange(line.id, 'secondaryEmail', e.target.value)}
                                                                    onKeyDown={(e) => this.handleVendorBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                    <Tooltip title="Remove Line">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="secondary"
                                                                            onClick={() => this.removeVendorBulkLine(line.id)}
                                                                            tabIndex={-1}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                
                                {/* Edit/Save/Cancel buttons below table */}
                                <div style={{marginTop: 20, textAlign: 'center'}}>
                                    {!this.state.showVendorBulkEdit ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                                this.setState({
                                                    showVendorBulkEdit: true,
                                                    pendingVendors: [{ id: uuid(), name: '', website: '', phoneNumber: '', address: '', primaryContact: '', primaryEmail: '', secondaryContact: '', secondaryEmail: '', paymentTermsId: null, isComplete: false }]
                                                });
                                                // Focus the first name field after a brief delay
                                                setTimeout(() => {
                                                    const nameField = document.querySelector('[data-vendor-line-id] input[name="name"]');
                                                    if (nameField) {
                                                        nameField.focus();
                                                    }
                                                }, 100);
                                            }}
                                            className="success-button-lg action-button"
                                            style={{minWidth: 100}}
                                        >
                                            Edit
                                        </Button>
                                    ) : (
                                        this.state.savingNewValue ? (
                                            <CircularProgress />
                                        ) : (
                                            <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                                                <Button 
                                                    onClick={this.saveAllVendorBulkChanges}
                                                    variant="contained"
                                                    color="primary"
                                                    className="success-button-lg"
                                                    disabled={false}
                                                >
                                                    Save All Changes
                                                </Button>
                                                <Button 
                                                    onClick={this.cancelVendorBulkEdit}
                                                    variant="contained"
                                                    className="cancel-button-lg"
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        )
                                    )}
                                </div>
                            </>
                        )}

                        {activeTab === 1 && (
                            <>
                                <div className={`${classes.toolbar} toolbar-responsive`} style={{ marginBottom: '20px' }}>
                                    <div className={`${classes.toolbarLeft} toolbar-left`} style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, maxWidth: '40%' }}>
                                        
                                    </div>
                                    <div className={`${classes.toolbarRight} toolbar-right`} style={{ flex: 1, maxWidth: '40%', justifyContent: 'flex-end' }}>
                                        
                                    </div>
                                </div>
                            <div className="parts-table-container" style={{maxWidth: 1200, textAlign: 'center'}}>
                                <table className="parts-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Description</th>
                                            <th>Days</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!this.state.showPaymentTermsBulkEdit ? (
                                            // Normal view - show existing payment terms
                                            paymentTerms.map((pt) => (
                                                <tr key={pt.id}>
                                                    <td>{pt.name}</td>
                                                    <td>{pt.description || ''}</td>
                                                    <td>{pt.days}</td>
                                                    <td>
                                                        <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                            <Tooltip title="Deactivate">
                                                                <IconButton
                                                                    size="small"
                                                                    color="secondary"
                                                                    onClick={() => this.handleDeletePaymentTerms(pt)}
                                                                    tabIndex={-1}
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            // Bulk edit view - show all payment terms in edit mode plus new lines
                                            <>
                                                {/* Existing payment terms in edit mode */}
                                                {paymentTerms.map((pt) => (
                                                    <tr key={pt.id}>
                                                        <td className={classes.editableCell}>
                                                            <TextField
                                                                value={pt.name}
                                                                onChange={(e) => this.handlePaymentTermsBulkFieldChange(pt.id, 'name', e.target.value)}
                                                                className={classes.editableInput}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </td>
                                                        <td className={classes.editableCell}>
                                                            <TextField
                                                                value={pt.description || ''}
                                                                onChange={(e) => this.handlePaymentTermsBulkFieldChange(pt.id, 'description', e.target.value)}
                                                                className={classes.editableInput}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </td>
                                                        <td className={classes.editableCell}>
                                                            <TextField
                                                                value={getNumberInputDisplayValue(pt.days)}
                                                                onChange={(e) => this.handlePaymentTermsBulkFieldChange(pt.id, 'days', e.target.value)}
                                                                className={classes.editableInput}
                                                                variant="outlined"
                                                                size="small"
                                                                type="number"
                                                            />
                                                        </td>
                                                        <td>
                                                            <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                <Tooltip title="Deactivate">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="secondary"
                                                                        onClick={() => this.handleDeletePaymentTerms(pt)}
                                                                        tabIndex={-1}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                                
                                                {/* New payment terms lines */}
                                                {this.state.pendingPaymentTerms.map((line, index) => (
                                                    <tr key={line.id} data-payment-terms-line-id={line.id} style={{backgroundColor: '#f5f5f5'}}>
                                                        <td className={classes.editableCell}>
                                                            <TextField
                                                                name="name"
                                                                value={line.name}
                                                                onChange={(e) => this.handlePaymentTermsBulkChange(line.id, 'name', e.target.value)}
                                                                onKeyDown={(e) => this.handlePaymentTermsBulkKeyDown(line.id, e)}
                                                                className={classes.editableInput}
                                                                variant="outlined"
                                                                size="small"
                                                                autoFocus={index === this.state.pendingPaymentTerms.length - 1}
                                                            />
                                                        </td>
                                                        <td className={classes.editableCell}>
                                                            <TextField
                                                                name="description"
                                                                value={line.description}
                                                                onChange={(e) => this.handlePaymentTermsBulkChange(line.id, 'description', e.target.value)}
                                                                onKeyDown={(e) => this.handlePaymentTermsBulkKeyDown(line.id, e)}
                                                                className={classes.editableInput}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </td>
                                                        <td className={classes.editableCell}>
                                                            <TextField
                                                                name="days"
                                                                value={getNumberInputDisplayValue(line.days)}
                                                                onChange={(e) => this.handlePaymentTermsBulkChange(line.id, 'days', e.target.value)}
                                                                onKeyDown={(e) => this.handlePaymentTermsBulkKeyDown(line.id, e)}
                                                                className={classes.editableInput}
                                                                variant="outlined"
                                                                size="small"
                                                                type="number"
                                                            />
                                                        </td>
                                                        <td>
                                                            <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                <Tooltip title="Remove Line">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="secondary"
                                                                        onClick={() => this.removePaymentTermsBulkLine(line.id)}
                                                                        tabIndex={-1}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Edit/Save/Cancel buttons below table */}
                            <div style={{marginTop: 20, textAlign: 'center'}}>
                                {!this.state.showPaymentTermsBulkEdit ? (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            this.setState({
                                                showPaymentTermsBulkEdit: true,
                                                pendingPaymentTerms: [{ id: uuid(), name: '', description: '', days: '', isComplete: false }]
                                            });
                                            // Focus the first name field after a brief delay
                                            setTimeout(() => {
                                                const nameField = document.querySelector('[data-payment-terms-line-id] input[name="name"]');
                                                if (nameField) {
                                                    nameField.focus();
                                                }
                                            }, 100);
                                        }}
                                        className="success-button-lg action-button"
                                        style={{minWidth: 100}}
                                    >
                                        Edit
                                    </Button>
                                ) : (
                                    this.state.savingNewValue ? (
                                        <CircularProgress />
                                    ) : (
                                        <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                                            <Button 
                                                onClick={this.saveAllPaymentTermsBulkChanges}
                                                variant="contained"
                                                color="primary"
                                                className="success-button-lg"
                                                disabled={false}
                                            >
                                                Save All Changes
                                            </Button>
                                            <Button 
                                                onClick={this.cancelPaymentTermsBulkEdit}
                                                variant="contained"
                                                className="cancel-button-lg"
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    )
                                )}
                            </div>
                        </>
                    )}

                            {activeTab === 2 && (
                                <>
                                    <div className={`${classes.toolbar} toolbar-responsive`}>
                                        <div className={`${classes.toolbarLeft} toolbar-left`} style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, maxWidth: '40%' }}>
                                            <TextField
                                                value={searchTerm}
                                                onChange={this.searchVendor}
                                                label="Search Dealers..."
                                                variant="outlined"
                                                className="search-field"
                                                style={{minWidth: '300px'}}
                                            />
                                        </div>
                                        <div className={`${classes.toolbarRight} toolbar-right`} style={{ flex: 1, maxWidth: '40%', justifyContent: 'flex-end' }}>
                                            
                                        </div>
                                    </div>
                                <center>
                                    <TablePagination
                                        rowsPerPageOptions={[50, 100, 200, 500]}
                                        colSpan={10}
                                        count={sortedDealers.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        slotProps={{
                                            select: {
                                                'aria-label': 'rows per page',
                                            },
                                            actions: {
                                                showFirstButton: true,
                                                showLastButton: true,
                                                slots: {
                                                    firstPageIcon: FirstPageIcon,
                                                    lastPageIcon: LastPageIcon,
                                                    nextPageIcon: ChevronRightIcon,
                                                    backPageIcon: ChevronLeftIcon,
                                                },
                                            },
                                        }}
                                        onChangePage={this.handleChangePage}
                                        onChangeRowsPerPage={this.handleChangeRowsPerPage}
                                    />
                                </center>
                                <div className="parts-table-container">
                                    <table className="parts-table">
                                        <thead>
                                            <tr>
                                                <SortButton field="source">Source</SortButton>
                                                <th>Website</th>
                                                <th>Phone</th>
                                                <th>Address</th>
                                                <th>Primary Contact</th>
                                                <th>Primary Email</th>
                                                <th>Secondary Contact</th>
                                                <th>Secondary Email</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!this.state.showDealerBulkEdit ? (
                                                // Normal view - show existing dealers
                                                paginatedDealers.map((dealer) => (
                                                    <tr key={dealer.id}>
                                                        <td>{dealer.source}</td>
                                                        <td>
                                                            {dealer.website && dealer.website.length > 0 ? 
                                                                <a href={dealer.website} target="_blank" rel="noopener noreferrer">{dealer.website}</a> : 
                                                                ''
                                                            }
                                                        </td>
                                                        <td>{dealer.phoneNumber || ''}</td>
                                                        <td>{dealer.address || ''}</td>
                                                        <td>{dealer.primaryContact || ''}</td>
                                                        <td>{dealer.primaryEmail || ''}</td>
                                                        <td>{dealer.secondaryContact || ''}</td>
                                                        <td>{dealer.secondaryEmail || ''}</td>
                                                        <td>
                                                            <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                <Tooltip title="Delete">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="secondary"
                                                                        onClick={() => this.handleDeleteDealer(dealer)}
                                                                        tabIndex={-1}
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                // Bulk edit view - show all dealers in edit mode plus new lines
                                                <>
                                                    {/* Existing dealers in edit mode */}
                                                    {paginatedDealers.map((dealer) => (
                                                        <tr key={dealer.id}>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={dealer.source}
                                                                    onChange={(e) => this.handleDealerBulkFieldChange(dealer.id, 'source', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={dealer.website || ''}
                                                                    onChange={(e) => this.handleDealerBulkFieldChange(dealer.id, 'website', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={dealer.phoneNumber || ''}
                                                                    onChange={(e) => this.handleDealerBulkFieldChange(dealer.id, 'phoneNumber', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={dealer.address || ''}
                                                                    onChange={(e) => this.handleDealerBulkFieldChange(dealer.id, 'address', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={dealer.primaryContact || ''}
                                                                    onChange={(e) => this.handleDealerBulkFieldChange(dealer.id, 'primaryContact', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={dealer.primaryEmail || ''}
                                                                    onChange={(e) => this.handleDealerBulkFieldChange(dealer.id, 'primaryEmail', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={dealer.secondaryContact || ''}
                                                                    onChange={(e) => this.handleDealerBulkFieldChange(dealer.id, 'secondaryContact', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    value={dealer.secondaryEmail || ''}
                                                                    onChange={(e) => this.handleDealerBulkFieldChange(dealer.id, 'secondaryEmail', e.target.value)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                    <Tooltip title="Delete">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="secondary"
                                                                            onClick={() => this.handleDeleteDealer(dealer)}
                                                                            tabIndex={-1}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    
                                                    {/* New dealer lines */}
                                                    {this.state.pendingDealers.map((line, index) => (
                                                        <tr key={line.id} data-dealer-line-id={line.id} style={{backgroundColor: '#f5f5f5'}}>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="source"
                                                                    value={line.source}
                                                                    onChange={(e) => this.handleDealerBulkChange(line.id, 'source', e.target.value)}
                                                                    onKeyDown={(e) => this.handleDealerBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    autoFocus={index === this.state.pendingDealers.length - 1}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="website"
                                                                    value={line.website}
                                                                    onChange={(e) => this.handleDealerBulkChange(line.id, 'website', e.target.value)}
                                                                    onKeyDown={(e) => this.handleDealerBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="phoneNumber"
                                                                    value={line.phoneNumber}
                                                                    onChange={(e) => this.handleDealerBulkChange(line.id, 'phoneNumber', e.target.value)}
                                                                    onKeyDown={(e) => this.handleDealerBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="address"
                                                                    value={line.address}
                                                                    onChange={(e) => this.handleDealerBulkChange(line.id, 'address', e.target.value)}
                                                                    onKeyDown={(e) => this.handleDealerBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="primaryContact"
                                                                    value={line.primaryContact}
                                                                    onChange={(e) => this.handleDealerBulkChange(line.id, 'primaryContact', e.target.value)}
                                                                    onKeyDown={(e) => this.handleDealerBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="primaryEmail"
                                                                    value={line.primaryEmail}
                                                                    onChange={(e) => this.handleDealerBulkChange(line.id, 'primaryEmail', e.target.value)}
                                                                    onKeyDown={(e) => this.handleDealerBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="secondaryContact"
                                                                    value={line.secondaryContact}
                                                                    onChange={(e) => this.handleDealerBulkChange(line.id, 'secondaryContact', e.target.value)}
                                                                    onKeyDown={(e) => this.handleDealerBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td className={classes.editableCell}>
                                                                <TextField
                                                                    name="secondaryEmail"
                                                                    value={line.secondaryEmail}
                                                                    onChange={(e) => this.handleDealerBulkChange(line.id, 'secondaryEmail', e.target.value)}
                                                                    onKeyDown={(e) => this.handleDealerBulkKeyDown(line.id, e)}
                                                                    className={classes.editableInput}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline={true}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className={classes.actionButtons} style={{justifyContent: 'center'}}>
                                                                    <Tooltip title="Remove Line">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="secondary"
                                                                            onClick={() => this.removeDealerBulkLine(line.id)}
                                                                            tabIndex={-1}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                
                                {/* Edit/Save/Cancel buttons below table */}
                                <div style={{marginTop: 20, textAlign: 'center'}}>
                                    {!this.state.showDealerBulkEdit ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                                this.setState({
                                                    showDealerBulkEdit: true,
                                                    pendingDealers: [{ id: uuid(), source: '', website: '', phoneNumber: '', address: '', primaryContact: '', primaryEmail: '', secondaryContact: '', secondaryEmail: '', isComplete: false }]
                                                });
                                                // Focus the first source field after a brief delay
                                                setTimeout(() => {
                                                    const sourceField = document.querySelector('[data-dealer-line-id] input[name="source"]');
                                                    if (sourceField) {
                                                        sourceField.focus();
                                                    }
                                                }, 100);
                                            }}
                                            className="success-button-lg action-button"
                                            style={{minWidth: 100}}
                                        >
                                            Edit
                                        </Button>
                                    ) : (
                                        this.state.savingNewValue ? (
                                            <CircularProgress />
                                        ) : (
                                            <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                                                <Button 
                                                    onClick={this.saveAllDealerBulkChanges}
                                                    variant="contained"
                                                    color="primary"
                                                    className="success-button-lg"
                                                    disabled={false}
                                                >
                                                    Save All Changes
                                                </Button>
                                                <Button 
                                                    onClick={this.cancelDealerBulkEdit}
                                                    variant="contained"
                                                    className="cancel-button-lg"
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        )
                                    )}
                                </div>
                            </>
                        )}

                        {activeTab === 3 && (
                            <>
                                <div style={{ marginTop: '20px' }}>
                                    <h3 style={{ marginBottom: '16px', fontSize: '1.25rem', fontWeight: 500 }}>
                                        Manage Brands
                                    </h3>
                                    <p style={{ marginBottom: '24px', color: '#666' }}>
                                        Click on any brand name to edit it. Changes will apply to all parts, assemblies, and subassemblies using that brand.
                                    </p>
                                    
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                                        {brands.map((brand, index) => (
                                            <div 
                                                key={index}
                                                onClick={() => this.handleBrandClick(brand)}
                                                style={{
                                                    padding: '16px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    backgroundColor: '#fff',
                                                    '&:hover': {
                                                        backgroundColor: '#f5f5f5',
                                                        borderColor: '#999'
                                                    }
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                    e.currentTarget.style.borderColor = '#999';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#fff';
                                                    e.currentTarget.style.borderColor = '#ddd';
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span style={{ fontWeight: 500, fontSize: '14px' }}>{brand}</span>
                                                    <FontAwesomeIcon icon={faEdit} style={{ color: '#666', fontSize: '12px' }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {brands.length === 0 && (
                                        <div style={{ 
                                            padding: '48px', 
                                            textAlign: 'center', 
                                            color: '#999',
                                            backgroundColor: '#f9f9f9',
                                            borderRadius: '4px',
                                            border: '1px dashed #ddd'
                                        }}>
                                            No brands found. Brands will appear here once you assign them to parts or assemblies.
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        </Box>
                        
                        <DrawerMenuRight
                            title="Edit Brand"
                            open={editBrandOpen}
                            customWidth={500}
                            hasUnsavedChanges={newBrandValue !== oldBrandValue}
                            onClose={() => this.setState({ editBrandOpen: false, newBrandValue: '', oldBrandValue: '' })}
                        >
                            <center>
                                <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px', maxWidth: '90%' }}>
                                    Renaming this brand will update all parts, assemblies, and subassemblies that currently use "{oldBrandValue}".
                                </p>
                                <TextField
                                    label="Brand Name"
                                    value={newBrandValue}
                                    onChange={(e) => this.setState({ newBrandValue: e.target.value })}
                                    variant="outlined"
                                    style={{ width: '90%', marginBottom: '20px' }}
                                    disabled={savingBrand}
                                    autoFocus
                                />
                                <br /><br />
                                {savingBrand ? (
                                    <CircularProgress />
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={this.saveBrand}
                                        className="success-button-lg"
                                    >
                                        Save
                                    </Button>
                                )}
                            </center>
                        </DrawerMenuRight>

                        <DrawerMenuRight
                            title="Bulk Lead Time by Vendor"
                            customWidth={900}
                            open={this.state.bulkLeadTimeDrawerOpen}
                            onClose={this.closeBulkLeadTimeDrawer}
                            disableEnterKeySubmit
                            hasUnsavedChanges={
                                this.state.bulkLeadTimeWeeks !== '' ||
                                this.state.bulkLeadTimeConfirmText !== '' ||
                                this.state.bulkLeadTimeTargetParts.length > 0
                            }
                        >
                            <div style={{ padding: '20px' }}>
                                <FormControl variant="outlined" style={{ width: '100%', marginBottom: 20 }}>
                                    <InputLabel>Vendor</InputLabel>
                                    <Select
                                        value={this.state.bulkLeadTimeVendorId || ''}
                                        onChange={this.handleBulkLeadTimeVendorChange}
                                        label="Vendor"
                                    >
                                        <MenuItem value="">
                                            <em>Select vendor</em>
                                        </MenuItem>
                                        {vendorLeadTimeOptions.map((vendor) => (
                                            <MenuItem key={vendor.id} value={vendor.id}>{vendor.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    value={this.state.bulkLeadTimeWeeks}
                                    onChange={(e) => this.setState({ bulkLeadTimeWeeks: e.target.value })}
                                    label="New Lead Time (weeks)"
                                    variant="outlined"
                                    type="number"
                                    inputProps={{ min: 1, max: 52, step: 1 }}
                                    style={{ width: '100%', marginBottom: 20 }}
                                />

                                <Typography variant="subtitle1" style={{ marginBottom: 10, fontWeight: 600 }}>
                                    Confirm the following list of parts will be updated ({bulkLeadTimeTargetParts.length})
                                </Typography>
                                <div className="parts-table-container" style={{ maxHeight: 600, overflowY: 'auto', marginBottom: 20 }}>
                                    <table className="parts-table">
                                        <thead>
                                            <tr>
                                                <th style={{ minWidth: 180 }}>Part</th>
                                                <th style={{ minWidth: 320 }}>Description</th>
                                                <th style={{ minWidth: 180 }}>Vendor</th>
                                                <th style={{ minWidth: 80 }}>Current</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bulkLeadTimeTargetParts.length === 0 ? (
                                                <tr>
                                                    <td colSpan={4}>No parts match the selected vendor.</td>
                                                </tr>
                                            ) : bulkLeadTimeTargetParts.map((partExpanded) => (
                                                <tr key={partExpanded.part.id}>
                                                    <td>{partExpanded.part.partId} rev {partExpanded.part.revision}</td>
                                                    <td className="text-left">{partExpanded.part.description}</td>
                                                    <td>{this.getVendorNameById(partExpanded.part.selectedVendorId) || '-'}</td>
                                                    <td>{partExpanded.part.leadTimeInWeeks}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <TextField
                                    value={this.state.bulkLeadTimeConfirmText}
                                    onChange={(e) => this.setState({ bulkLeadTimeConfirmText: e.target.value })}
                                    label='Type "UPDATE" to confirm'
                                    variant="outlined"
                                    style={{ width: '100%', marginBottom: 20 }}
                                />

                                {this.state.savingBulkLeadTime ? (
                                    <CircularProgress />
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={this.applyBulkLeadTimeUpdate}
                                        className="success-button-lg"
                                        disabled={bulkLeadTimeTargetParts.length === 0}
                                    >
                                        Apply Lead Time Update
                                    </Button>
                                )}
                            </div>
                        </DrawerMenuRight>
                    </div>
                </div>
            </DashboardTemplate>
        );
    }
}

let globalProps;

Lists.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Lists);